import buildResponse from 'src/internal/shared/buildResponse';
import Headers from 'src/internal/shared/headers/headers';

import type {
  FetchHookOptions,
  FetchOptions,
  FetchResult,
  FetchState,
  FetchUrl,
} from 'src/internal/shared/fetch/types';

type FetchHookOptionsAndSignal<TData> = FetchHookOptions<TData> & {
  signal?: AbortSignal;
};

type FetchOptionsAndSignal = FetchOptions & {
  signal?: AbortSignal;
};

type BasicState<TData> = FetchState<TData>;

type ObservabilityConfig = {
  expectedErrors?: readonly number[];
  // mutedErrors are currently only passed by legacy APIs.
  // There is a plan to remove this property in the future.
  // Check doc: https://docs.google.com/document/d/1YH9PgKmQieGFJ5K75-oVQyyUM3AdIhEiDOdK50fpulo/edit?tab=t.0#heading=h.ic1l8w4gfir1
  mutedErrors?: readonly number[];
};

type FetchLogFn = (
  res: Response,
  duration: number,
  observabilityConfig: ObservabilityConfig,
) => void;

export default function fullFetch<TData>(
  url: FetchUrl,
  options: FetchHookOptionsAndSignal<TData>,
  fetchFunction: (url: FetchUrl, options: FetchOptionsAndSignal) => FetchResult,
  fetchFormat: 'text' | 'json',
  fetchLog: FetchLogFn,
): Promise<BasicState<TData> | null> {
  return new Promise((resolve) => {
    (async (): Promise<BasicState<TData> | null> => {
      const start = Date.now();

      const {
        expectedErrors,
        mutedErrors,
        timeout,
        retry,
        signal,
        ...fetchOptions
      } = options;

      let abortController: AbortController | null = null;
      let timeoutId: ReturnType<typeof setTimeout> | null = null;

      // If the main signal controller is aborted, abort also the current request.
      signal?.addEventListener('abort', function abortListener() {
        resolve(null);

        abortController?.abort();
        abortController = null;

        signal?.removeEventListener('abort', abortListener);
      });

      let i = 0;

      /* eslint-disable no-await-in-loop, no-loop-func */
      do {
        abortController = new AbortController();

        const promiseData: Promise<BasicState<TData>> = (async (): Promise<
          BasicState<TData>
        > => {
          try {
            const res = await fetchFunction(url, {
              ...fetchOptions,
              signal: abortController.signal,
            });

            const data = await res[fetchFormat]();

            if (!res.ok) {
              fetchLog(res, Date.now() - start, {
                expectedErrors,
                mutedErrors,
              });
            }

            return {
              called: true,
              loading: false,
              error: undefined,
              status: res.status,
              headers: new Headers(res.headers),
              data,
            };
          } catch (err) {
            const error = err instanceof Error ? err : new Error(`${err}`);

            const networkErrorRes = buildResponse('', {
              status: -1,
              statusText: error.message,
              url,
            });

            fetchLog(networkErrorRes, Date.now() - start, {
              expectedErrors,
              mutedErrors,
            });

            return {
              called: true,
              loading: false,
              error,
              status: undefined,
              headers: undefined,
              data: undefined,
            };
          } finally {
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }
          }
        })();

        const promiseTimeout: Promise<BasicState<TData>> = (async (): Promise<
          BasicState<TData>
        > => {
          const err = new Error(`Fetch exceeded timeout of ${timeout} ms`);

          await new Promise((res) => {
            timeoutId = setTimeout(res, timeout || 2147483647);
          });

          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }

          abortController?.abort();

          const networkErrorRes = buildResponse('', {
            status: -1,
            statusText: err.message,
            url,
          });

          fetchLog(networkErrorRes, Date.now() - start, {
            expectedErrors,
            mutedErrors,
          });

          return {
            called: true,
            loading: false,
            error: err,
            status: undefined,
            headers: undefined,
            data: undefined,
          };
        })();

        const result: BasicState<TData> = await Promise.race([
          promiseData,
          promiseTimeout,
        ]);

        // If there's been an abort error, return null to signal the abortion.
        if (result.error?.name === 'AbortError') {
          return null;
        }

        // If we do not want to retry, return the result.
        if (!(await retry?.(result, i))) {
          return result;
        }

        i++;
      } while (true);
      /* eslint-enable */
    })().then(resolve);
  });
}
